import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Wordmark from "@/components/Wordmark";
import { Loader2, ChevronRight, ChevronLeft, Check, User, Sparkles, MapPin, Award, CalendarClock, PartyPopper, Info, ShieldCheck, ImagePlus, BadgePoundSterling, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import ServicePricingEditor from "@/components/ServicePricingEditor";
import DbsVerifyCta from "@/components/DbsVerifyCta";
import PayoutsPanel from "@/components/PayoutsPanel";

const STEPS = [
  { title: "About you", icon: User },
  { title: "Your services", icon: Sparkles },
  { title: "Your pricing", icon: BadgePoundSterling },
  { title: "Service areas", icon: MapPin },
  { title: "Experience", icon: Award },
  { title: "DBS", icon: ShieldCheck },
  { title: "Availability", icon: CalendarClock },
];

export default function CleanerOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    phone: "", town: "", type: "independent", company_name: "",
    services: [], service_areas: [],
    years_experience: 1, bio: "", languages: ["English"], photo_url: "",
  });
  const [pricing, setPricing] = useState({});
  const [pricingStatus, setPricingStatus] = useState({ complete: false, incomplete: [] });
  const [dbs, setDbs] = useState(null);
  const [phoneOnFile, setPhoneOnFile] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [work, setWork] = useState([]);
  const [workBusy, setWorkBusy] = useState(false);
  const [coverageMet, setCoverageMet] = useState(false);
  const [availError, setAvailError] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.services));
    api.get("/cleaners/me").then((r) => {
      if (r.data.onboarded) navigate("/cleaner/dashboard", { replace: true });
      setForm((f) => ({ ...f, photo_url: r.data.photo_url || "", town: f.town || r.data.town || "" }));
      setWork(r.data.gallery || []);
    }).catch(() => {});
    api.get("/auth/me").then((r) => {
      const ph = r.data.phone || r.data.user?.phone || "";
      setPhoneOnFile(ph);
      setForm((f) => ({ ...f, phone: f.phone || ph }));
    }).catch(() => {});
    api.get("/dbs/mine").then((r) => setDbs(r.data)).catch(() => {});
  }, [navigate]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addArea = (a) => setForm((f) => f.service_areas.some((x) => x.place_id === a.place_id)
    ? f : { ...f, service_areas: [...f.service_areas, a] });
  const removeArea = (id) => setForm((f) => ({ ...f, service_areas: f.service_areas.filter((a) => a.place_id !== id) }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/cleaners/me/photo", body, { headers: { "Content-Type": "multipart/form-data" } });
      upd("photo_url", data.photo_url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPhotoBusy(false); e.target.value = ""; }
  };

  const uploadWork = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setWorkBusy(true);
    const body = new FormData();
    files.forEach((f) => body.append("files", f));
    try {
      const { data } = await api.post("/cleaners/me/gallery", body, { headers: { "Content-Type": "multipart/form-data" } });
      setWork(data.gallery);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setWorkBusy(false); e.target.value = ""; }
  };

  const removeWork = async (url) => {
    const id = url.split("/").pop();
    try {
      const { data } = await api.delete(`/cleaners/me/gallery/${id}`);
      setWork(data.gallery);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleService = (slug) => upd("services", form.services.includes(slug)
    ? form.services.filter((s) => s !== slug) : [...form.services, slug]);
  const allSelected = services.length > 0 && form.services.length === services.length;
  const selectAll = () => upd("services", allSelected ? [] : services.map((s) => s.slug));

  const declareNoDbs = async () => {
    try {
      await api.post("/dbs/declare", { declaration: "no_dbs" });
      const { data } = await api.get("/dbs/mine");
      setDbs(data);
      toast.success("Noted — you can add DBS verification any time from your dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const canNext = () => {
    if (step === 0) return form.phone.trim().length >= 10 && form.town.trim() && !!form.photo_url
      && (form.type === "independent" || form.company_name.trim());
    if (step === 1) return form.services.length > 0;
    if (step === 2) return pricingStatus.complete;
    if (step === 3) return form.service_areas.length > 0;
    if (step === 4) return form.bio.trim().length > 10;
    if (step === 5) return true;            // DBS is always optional
    if (step === 6) return coverageMet;     // at least 2 months of availability
    return true;
  };

  const next = () => {
    if (canNext()) { setAvailError(false); setStep(step + 1); return; }
    if (step === 2) toast.error("Please finish the pricing for every service you selected");
  };

  const savePricing = async () => {
    await api.put("/cleaners/me/pricing", { pricing, services: form.services });
  };

  const finish = async () => {
    if (!coverageMet) { setAvailError(true); return; }
    setSaving(true);
    try {
      await savePricing();
      await api.put("/cleaners/me", {
        phone: form.phone, town: form.town, type: form.type,
        company_name: form.type === "company" ? form.company_name : null,
        services: form.services, service_areas: form.service_areas,
        years_experience: form.years_experience, bio: form.bio,
        languages: form.languages, onboarded: true,
        availability: { max_jobs_per_day: 3, travel_buffer_min: 30 },
      });
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="glass border-b border-primary/5 h-[72px] flex items-center px-5 sm:px-8">
          <Wordmark iconClass="h-9 w-auto" textClass="text-lg sm:text-xl" />
        </header>
        <div className="flex-1 flex items-start justify-center px-5 py-12">
          <div className="max-w-xl w-full text-center" data-testid="onboard-complete">
            <PartyPopper className="h-12 w-12 text-accent mx-auto" />
            <h1 className="font-display text-3xl font-semibold tracking-tight mt-5">Profile completed! 🎉🎊</h1>
            <p className="font-display text-lg mt-1">Welcome to My Clean Connect™</p>
            <p className="text-muted-foreground mt-3">
              Your profile is live. Customers can now find and book you when your services, areas
              and confirmed availability match what they're searching for.
            </p>
            <div className="mt-7 text-left"><PayoutsPanel compact /></div>
            <button onClick={() => navigate("/cleaner/dashboard", { replace: true })}
              className="mt-6 px-6 py-3 rounded-full border border-border font-medium hover:bg-secondary transition-colors"
              data-testid="onboard-go-dashboard">Go to my dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="glass sticky top-0 z-40 border-b border-primary/5 h-[72px] flex items-center px-5 sm:px-8">
        <Wordmark iconClass="h-9 w-auto" textClass="text-lg sm:text-xl" />
      </header>

      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <div className="flex items-center gap-1.5 mb-8">
          {STEPS.map((s, i) => (
            <div key={s.title} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-accent" : "bg-border"}`} />
          ))}
        </div>
        <p className="text-sm text-muted-foreground mb-1">Step {step + 1} of {STEPS.length}</p>
        <h1 className="font-display text-3xl font-semibold tracking-tight mb-6">
          {step === 0 && `Welcome, ${user?.name?.split(" ")[0] || "there"} 👋`}
          {step === 1 && "What do you clean?"}
          {step === 2 && "Set your prices"}
          {step === 3 && "Where do you work?"}
          {step === 4 && "Show off your experience"}
          {step === 5 && "DBS verification"}
          {step === 6 && "Your availability calendar"}
        </h1>

        <div className="bg-card rounded-2xl border border-primary/5 p-6 sm:p-8">
          {step === 0 && (
            <div className="space-y-5" data-testid="onboard-step-0">
              <p className="text-muted-foreground -mt-2">Let's set up your cleaner profile. Your profile goes live when you finish and submit.</p>
              <Field label="Mobile number">
                <input value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="07xxx xxxxxx" className="cc-input" data-testid="onboard-phone" />
                <p className="text-xs text-muted-foreground mt-1">
                  {phoneOnFile ? "Taken from your registration — you only enter this once." : "You'll only be asked for this once."}
                  {" "}Mobile verification by text is coming soon.
                </p>
              </Field>
              <Field label="Profile photo (required)">
                <div className="flex items-center gap-4 mt-2">
                  {form.photo_url
                    ? <img src={form.photo_url} alt="" className="h-20 w-20 rounded-2xl object-cover border border-border" data-testid="onboard-photo-preview" />
                    : <div className="h-20 w-20 rounded-2xl border-2 border-dashed border-border flex items-center justify-center text-muted-foreground"><ImagePlus className="h-6 w-6" /></div>}
                  <div>
                    <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent text-white text-sm font-medium hover:bg-accent/90 transition-colors cursor-pointer" data-testid="onboard-photo-label">
                      {photoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                      {form.photo_url ? "Change photo" : "Upload photo"}
                      <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="onboard-photo-input" />
                    </label>
                    <p className="text-xs text-muted-foreground mt-2 max-w-sm">
                      A clear, recent photo of you helps customers recognise and trust the person
                      they're booking. This is your public profile photo — it does not verify your
                      identity.
                    </p>
                  </div>
                </div>
              </Field>
              <Field label="Town / City you're based in">
                <input value={form.town} onChange={(e) => upd("town", e.target.value)} placeholder="e.g. Birmingham" className="cc-input" data-testid="onboard-town" />
              </Field>
              <Field label="Are you an individual or a company?">
                <div className="grid grid-cols-2 gap-3 mt-1">
                  {["independent", "company"].map((t) => (
                    <button key={t} onClick={() => upd("type", t)} className={`p-4 rounded-xl border-2 text-left capitalize transition-colors ${form.type === t ? "border-accent bg-accent/5" : "border-border"}`} data-testid={`onboard-type-${t}`}>
                      {t === "independent" ? "Independent cleaner" : "Cleaning company"}
                    </button>
                  ))}
                </div>
              </Field>
              {form.type === "company" && (
                <Field label="Company name"><input value={form.company_name} onChange={(e) => upd("company_name", e.target.value)} className="cc-input" data-testid="onboard-company" /></Field>
              )}
            </div>
          )}

          {step === 1 && (
            <div data-testid="onboard-step-1">
              <p className="text-muted-foreground mb-3">
                Select every service you offer. You set your own price for each one on the next step.
              </p>
              <button onClick={selectAll}
                className={`mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm font-medium transition-colors ${allSelected ? "border-accent bg-accent/10 text-accent" : "border-border hover:border-accent/40"}`}
                data-testid="onboard-select-all-services">
                <CheckCheck className="h-4 w-4" /> {allSelected ? "Deselect all services" : "Select all services"}
              </button>
              <div className="grid sm:grid-cols-2 gap-3">
                {services.map((s) => (
                  <button key={s.slug} onClick={() => toggleService(s.slug)} className={`text-left p-4 rounded-xl border-2 transition-colors ${form.services.includes(s.slug) ? "border-accent bg-accent/5" : "border-border hover:border-accent/40"}`} data-testid={`onboard-service-${s.slug}`}>
                    <p className="font-medium inline-flex items-center gap-2">
                      {form.services.includes(s.slug) && <Check className="h-4 w-4 text-accent" />}{s.name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Selecting all services is just a shortcut — you still set a separate, different
                price for each service, and you can untick any of them.
              </p>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboard-step-2">
              <p className="text-muted-foreground mb-4">
                You're in control of every price. Only the services you chose appear here.
              </p>
              <ServicePricingEditor services={form.services} pricing={pricing}
                onChange={setPricing} onStatus={setPricingStatus} />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="onboard-step-3">
              <Field label="Where do you provide cleaning services?">
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  Add every town, city, area or county you cover — start typing and pick the
                  suggestion. You can add as many as you like, which is ideal if you or your team
                  work across several places.
                </p>
                <LocationAutocomplete onSelect={addArea} testid="onboard-area" />
                {form.service_areas.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="onboard-area-chips">
                    {form.service_areas.map((a) => (
                      <span key={a.place_id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-medium" data-testid="onboard-area-chip">
                        {a.label}
                        <button type="button" onClick={() => removeArea(a.place_id)} className="hover:text-destructive" data-testid="onboard-area-remove">×</button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-3">
                  Customers search by place, so no postcode lists or travel radius needed. Your own
                  address is never shown publicly.
                </p>
              </Field>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5" data-testid="onboard-step-4">
              <Field label={`Years of experience: ${form.years_experience}`}>
                <input type="range" min="0" max="30" value={form.years_experience} onChange={(e) => upd("years_experience", parseInt(e.target.value))} className="w-full accent-[#4C9E22]" data-testid="onboard-years" />
              </Field>
              <Field label="Short bio">
                <textarea value={form.bio} onChange={(e) => upd("bio", e.target.value)} rows={4} placeholder="Tell customers about yourself, your standards and what makes you great…" className="cc-input" data-testid="onboard-bio" />
              </Field>
              <Field label="Photos of your previous work (optional)">
                <p className="text-xs text-muted-foreground mt-1">
                  Optional. Add photos of cleaning work you've completed elsewhere to show the
                  quality and type of work you do. They appear as a small gallery on your public
                  profile. These are not identity or DBS evidence.
                </p>
                {work.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3" data-testid="onboard-work-gallery">
                    {work.map((u) => (
                      <div key={u} className="relative group">
                        <img src={u} alt="" className="h-20 w-full object-cover rounded-xl border border-border" />
                        <button type="button" onClick={() => removeWork(u)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/90 border border-border text-xs" data-testid="onboard-work-remove">×</button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors cursor-pointer" data-testid="onboard-work-label">
                  {workBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Add work photos
                  <input type="file" accept="image/*" multiple className="hidden" onChange={uploadWork} data-testid="onboard-work-input" />
                </label>
              </Field>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4" data-testid="onboard-step-5">
              <DbsVerifyCta dbs={dbs} onChange={setDbs} variant="onboarding" />
              <button onClick={declareNoDbs} className="text-sm text-muted-foreground underline" data-testid="onboard-dbs-none">
                I don't have a DBS
              </button>
              <p className="text-xs text-muted-foreground">
                DBS verification never blocks your registration and you can complete it later from
                your dashboard. My Clean Connect™ only shows a DBS Verified badge once a
                verification result has been confirmed.
              </p>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-4" data-testid="onboard-step-6">
              <div className="flex gap-3 p-4 rounded-xl bg-accent/10 text-sm">
                <Info className="h-5 w-5 text-accent shrink-0" />
                <p>
                  Mark the actual dates and times you're free. <strong>Any dates you do not mark as
                  available will not be offered to customers.</strong> You don't have to be
                  available every day — just make sure your schedule covers at least the next
                  2 months. Move between months freely; nothing is lost until you confirm.
                </p>
              </div>
              <AvailabilityCalendar showCoverageError={availError}
                onChange={(d) => { setCoverageMet(!!d.coverage_met); if (d.coverage_met) setAvailError(false); }} />
            </div>
          )}

          <div className="flex justify-between mt-8">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full border border-border font-medium disabled:opacity-40 hover:bg-secondary transition-colors" data-testid="onboard-back">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button onClick={next} disabled={!canNext()} className="inline-flex items-center gap-1 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium disabled:opacity-40 hover:bg-primary/90 transition-colors" data-testid="onboard-next">
                Continue <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={finish} disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-accent text-white font-medium disabled:opacity-40 hover:bg-accent/90 transition-colors" data-testid="onboard-finish">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PartyPopper className="h-4 w-4" />} Finish & Submit
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`.cc-input{width:100%;margin-top:0.25rem;padding:0.7rem 0.85rem;border-radius:0.75rem;border:1px solid hsl(var(--border));background:hsl(var(--background));outline:none}.cc-input:focus{border-color:hsl(var(--accent))}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-sm font-medium">{label}</label>{children}</div>;
}
